import { useRef, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import parse from 'html-react-parser';
import { Modal } from '@diamond-design/components';
import { Check } from '@diamond-design/icons';
import http from 'lc-http';
import FormContainer from '../formContainer/FormContainer';
import AddressConfirmModal from './AddressConfirmModal';
import CtaButton from '../ctaButton/ctaButton';
import styles from '../formContainer/FormContainer.module.scss';
import containerStyles from './DMOptOutFormContainer.module.scss';
import DM_OPT_OUT_FIELD_CONFIGS from './dmOptOutFormFields';

const API_ENDPOINT = '/api/forms/dmoptout';
const SMARTY_URL = 'https://us-street.api.smartystreets.com/street-address';
const SMARTY_AUTOCOMPLETE_URL =
  'https://us-autocomplete-pro.api.smartystreets.com/lookup';
const SMARTY_HTTP_CONFIG = { useCsrf: false };
const normalize = (s) => s?.toUpperCase().trim() ?? '';

const UI_FIELD_CONFIG = {
  firstName: { type: 'text', colSpan: 6, maxLength: 50 },
  middleName: { type: 'text', colSpan: 3, maxLength: 50 },
  lastName: { type: 'text', colSpan: 3, maxLength: 50 },
  street: {
    type: 'autocomplete',
    colSpan: 6,
    maxLength: 500,
    autocompleteDisplayKey: 'street_line',
    autocompleteOptionKeys: ['street_line', 'city', 'state', 'zipcode'],
    autocompleteFieldMap: {
      street_line: 'street',
      city: 'city',
      state: 'state',
      zipcode: 'zip',
    },
  },
  city: { type: 'text', colSpan: 2, maxLength: 50 },
  state: { type: 'text', colSpan: 2, maxLength: 50 },
  zip: { type: 'text', colSpan: 2, maxLength: 5 },
};

const FIELDS = DM_OPT_OUT_FIELD_CONFIGS.map((config) => ({
  key: config.name,
  ...config,
  ...UI_FIELD_CONFIG[config.name],
}));

export default function DMOptOutFormContainer({
  fieldLabels = {},
  modals = {},
  smartyKey = '',
  ...rest
}) {
  const formRef = useRef(null);
  const [activeModal, setActiveModal] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmAddressData, setConfirmAddressData] = useState(null);
  const [pendingFormValues, setPendingFormValues] = useState(null);
  const [isConfirming, setIsConfirming] = useState(false);
  const [isBypassingSmarty, setIsBypassingSmarty] = useState(false);

  const mergedFields = useMemo(
    () =>
      FIELDS.map((field) => ({
        ...field,
        label: fieldLabels[field.key]?.label || field.key,
        placeholder: fieldLabels[field.key]?.placeholder || '',
      })),
    [fieldLabels]
  );

  const handleAutocomplete = async (field, inputValue) => {
    const url = `${SMARTY_AUTOCOMPLETE_URL}?key=${smartyKey}&search=${encodeURIComponent(
      inputValue
    )}`;
    const res = await http.get(url, SMARTY_HTTP_CONFIG);
    return res.data?.suggestions ?? [];
  };

  const handleSubmit = async (values) => {
    try {
      const params = new URLSearchParams({
        key: smartyKey,
        street: values.street,
        city: values.city,
        state: values.state,
        zipcode: values.zip,
        candidates: 3,
      });
      const response = await http.get(
        `${SMARTY_URL}?${params.toString()}`,
        SMARTY_HTTP_CONFIG
      );
      const candidates = response.data ?? [];

      if (!candidates.length) {
        setPendingFormValues(values);
        setActiveModal('notFound');
        return;
      }

      const best = candidates[0];
      if (!best) {
        setPendingFormValues(values);
        setActiveModal('notFound');
        return;
      }

      const standardized = {
        street: best.delivery_line_1,
        city: best.components?.city_name,
        state: best.components?.state_abbreviation,
        zip: best.components?.zipcode,
      };

      const isExactMatch =
        normalize(standardized.street) === normalize(values.street) &&
        normalize(standardized.city) === normalize(values.city) &&
        normalize(standardized.state) === normalize(values.state) &&
        normalize(standardized.zip) === normalize(values.zip);

      if (!isExactMatch) {
        setConfirmAddressData({
          standardized,
          entered: {
            street: values.street,
            city: values.city,
            state: values.state,
            zip: values.zip,
          },
        });
        setPendingFormValues(values);
        setShowConfirmModal(true);
        return;
      }

      const apiResponse = await http.post(API_ENDPOINT, values);
      if (apiResponse.data?.outcome === 'confirmed') {
        formRef.current?.reset();
        setActiveModal('success');
      } else {
        setActiveModal('error');
      }
    } catch {
      setActiveModal('error');
    }
  };

  const handleConfirmContinue = async (chosenAddress) => {
    if (isConfirming) return;
    setIsConfirming(true);

    const payload = {
      ...pendingFormValues,
      street: chosenAddress.street,
      city: chosenAddress.city,
      state: chosenAddress.state,
      zip: chosenAddress.zip,
      confirmed: true,
    };

    setShowConfirmModal(false);
    setConfirmAddressData(null);
    setPendingFormValues(null);

    try {
      const response = await http.post(API_ENDPOINT, payload);
      const result = response.data;

      if (result.outcome === 'confirmed') {
        formRef.current?.reset();
        setActiveModal('success');
      } else {
        setActiveModal('error');
      }
    } catch {
      setActiveModal('error');
    } finally {
      setIsConfirming(false);
    }
  };

  const handleUseEnteredAddress = async () => {
    if (isBypassingSmarty) return;
    setIsBypassingSmarty(true);

    const payload = { ...pendingFormValues, confirmed: true };
    setActiveModal(null);
    setPendingFormValues(null);

    try {
      const response = await http.post(API_ENDPOINT, payload);
      const result = response.data;
      if (result.outcome === 'confirmed') {
        formRef.current?.reset();
        setActiveModal('success');
      } else {
        setActiveModal('error');
      }
    } catch {
      setActiveModal('error');
    } finally {
      setIsBypassingSmarty(false);
    }
  };

  const { modalTitle, modalButtonLabel, modalButtonVariant, modalContent } =
    useMemo(() => {
      const config = modals[activeModal] ?? {};
      const primaryAction =
        config.actions?.find((a) => a.variant === 'primary') ??
        config.actions?.[0];

      if (activeModal === 'success') {
        return {
          modalTitle: config.title,
          modalButtonLabel: primaryAction?.label || 'Ok',
          modalButtonVariant: primaryAction?.variant,
          modalContent: config.body || '',
        };
      }
      if (activeModal === 'notFound') {
        return {
          modalTitle: config.title,
          modalButtonLabel: primaryAction?.label || 'Edit Address',
          modalButtonVariant: primaryAction?.variant,
          modalContent: config.body || '',
        };
      }
      return {
        modalTitle: config.title,
        modalButtonLabel: primaryAction?.label || 'Try Again',
        modalButtonVariant: primaryAction?.variant,
        modalContent: config.body || '',
      };
    }, [activeModal, modals]);

  return (
    <>
      <FormContainer
        ref={formRef}
        {...rest}
        fields={mergedFields}
        onSubmit={handleSubmit}
        onAutocomplete={handleAutocomplete}
      />

      <AddressConfirmModal
        open={showConfirmModal}
        onContinue={handleConfirmContinue}
        onEditAddress={() => {
          setShowConfirmModal(false);
          setConfirmAddressData(null);
          setPendingFormValues(null);
        }}
        isLoading={isConfirming}
        standardized={confirmAddressData?.standardized}
        entered={confirmAddressData?.entered}
        title={modals.addressConfirm?.title}
        continueLabel={modals.addressConfirm?.actions?.[0]?.label}
        editAddressLabel={modals.addressConfirm?.actions?.[1]?.label}
      />

      <Modal
        open={activeModal !== null}
        onDismiss={() => setActiveModal(null)}
        title={modalTitle}
        {...(activeModal === 'success' && {
          showBadge: true,
          badgeColor: 'success',
          badgeIcon: Check,
        })}
        size="xs"
        childrenContainerStyle={styles.modalNoPaddingTop}
        actions={
          activeModal === 'notFound' && modals.notFound?.actions?.[0]
            ? []
            : [
                {
                  label: modalButtonLabel,
                  render: () => (
                    <CtaButton
                      type="button"
                      variant={modalButtonVariant}
                      size="lg"
                      fullWidth
                      label={modalButtonLabel}
                      onClick={() => setActiveModal(null)}
                    />
                  ),
                },
              ]
        }
      >
        {parse(modalContent)}
        {activeModal === 'notFound' && modals.notFound?.actions?.[0] && (
          <div className={containerStyles.modalButtonRow}>
            <CtaButton
              type="button"
              variant={modals.notFound.actions[0].variant}
              size="lg"
              fullWidth
              label={modals.notFound.actions[0].label}
              onClick={handleUseEnteredAddress}
              disabled={isBypassingSmarty}
            />
            <CtaButton
              type="button"
              variant={modalButtonVariant}
              size="lg"
              fullWidth
              label={modalButtonLabel}
              onClick={() => setActiveModal(null)}
            />
          </div>
        )}
      </Modal>
    </>
  );
}

DMOptOutFormContainer.propTypes = {
  smartyKey: PropTypes.string,
  fieldLabels: PropTypes.objectOf(
    PropTypes.shape({
      label: PropTypes.string,
      placeholder: PropTypes.string,
    })
  ),
  modals: PropTypes.objectOf(
    PropTypes.shape({
      title: PropTypes.string,
      body: PropTypes.string,
      actions: PropTypes.arrayOf(
        PropTypes.shape({
          label: PropTypes.string,
          variant: PropTypes.string,
        })
      ),
    })
  ),
};
